import React from 'react';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { EventItem } from 'queries/events/types';
import { findByAssetNameRecursive } from 'utils/Trail';
import { dateTime } from 'utils/DateUtil';
import { useAssetsByWorkspaceQuery } from 'queries/assets/queries';
import './style.scss';
import CopyButton from 'components/CopyButton/CopyButton';
import { AssetField } from 'components/Asset/AssetField';
import { IconButton } from '@mui/material';

type DetailName = keyof Omit<EventItem, 'children'> | 'duration';

const detailsList: Array<DetailName> = [
  'alarm_datetime_start',
  'alarm_datetime_end',
  'duration',
  'method',
  'category',
];
interface EventInfoProps {
  event: EventItem;
  className?: string;
}

const EventInfo: React.FC<EventInfoProps> = ({ event, className }) => {
  const { t } = useTranslation();
  const { data } = useAssetsByWorkspaceQuery();
  const { asset, is_able_to_operate } = event;
  const [scrollRight, setScrollRight] = React.useState(true);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const checkOverflow = () => {
      if (scrollRef.current) {
        setIsOverflowing(scrollRef.current.scrollWidth > scrollRef.current.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [event]);

  const eventAssetTree =
    findByAssetNameRecursive(data?.items ?? [], asset.name)?.breadcrumbPath ?? [];

  const trailWithoutThisAsset = eventAssetTree.filter((i) => i.name !== asset.name);
  trailWithoutThisAsset.pop();

  const getDetailValue = (detail: DetailName) => {
    switch (detail) {
      case 'alarm_datetime_end':
      case 'alarm_datetime_start':
        return event[detail] ? dateTime(event[detail]) : null;
      default:
        return event[detail];
    }
  };

  const handleArrowClick = () => {
    if (scrollRef.current) {
      if (scrollRight) {
        scrollRef.current.scrollTo({ left: scrollRef.current.scrollWidth, behavior: 'smooth' });
        setScrollRight(false);
      } else {
        scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        setScrollRight(true);
      }
    }
  };

  return (
    <div className={`event-details ${className}`}>
      <AssetField
        asset={event.asset}
        ableToOperate={
          is_able_to_operate ? { value: is_able_to_operate, readOnly: true } : undefined
        }
      />

      <div
        className={`event-details-scroll-container ${
          scrollRight
            ? 'event-details-scroll-container-right'
            : 'event-details-scroll-container-left'
        }`}
      >
        {isOverflowing && !scrollRight && (
          <IconButton
            onClick={handleArrowClick}
            className="event-details-scroll-button event-details-scroll-button-left"
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        <div className="event-details-scroll" ref={scrollRef}>
          <div className="event-detail">
            <CopyButton text={event.id.replace('-CLONE', '')} />
          </div>
          {detailsList.map((detail) => {
            const detailValue = getDetailValue(detail);
            if (!detailValue) return null;

            return (
              <div className="event-detail" key={detail}>
                <span className="event-label">{t(`event_page:event_details.${detail}`)}:</span>
                <span className={`event-value event-value-${detail}`}>{detailValue as string}</span>
              </div>
            );
          })}
        </div>
        {isOverflowing && scrollRight && (
          <IconButton
            onClick={handleArrowClick}
            className="event-details-scroll-button event-details-scroll-button-right"
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default EventInfo;
